import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AlertController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

interface VersionInfo {
  build: string;
  timestamp: string;
}

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  /** How often to check for a new version (5 minutes). */
  private readonly POLL_INTERVAL_MS = 5 * 60 * 1000;

  private currentBuild: string | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private alertOpen = false;

  constructor(
    private http: HttpClient,
    private alertCtrl: AlertController
  ) {}

  /**
   * Call once from AppComponent.ngOnInit().
   * Reads the build ID that was baked in at deploy time, then starts polling.
   */
  init(): void {
    this.fetchVersion().then(info => {
      if (info) {
        this.currentBuild = info.build;
      }
      this.startPolling();
    });
  }

  private startPolling(): void {
    this.pollTimer = setInterval(() => this.checkForUpdate(), this.POLL_INTERVAL_MS);
  }

  private async checkForUpdate(): Promise<void> {
    if (this.alertOpen) {
      return;
    }

    const info = await this.fetchVersion();
    if (!info || !this.currentBuild) {
      return;
    }

    if (info.build !== this.currentBuild) {
      await this.promptReload();
    }
  }

  /**
   * Fetches assets/version.json with a cache-busting query param so the
   * browser never serves a stale copy regardless of service-worker or
   * CDN settings.
   */
  private async fetchVersion(): Promise<VersionInfo | null> {
    const url = `assets/version.json?t=${Date.now()}`;
    try {
      return await firstValueFrom(this.http.get<VersionInfo>(url));
    } catch (err) {
      console.warn('[AppUpdateService] Could not fetch version.json', err);
      return null;
    }
  }

  private async promptReload(): Promise<void> {
    this.alertOpen = true;
    const alert = await this.alertCtrl.create({
      header: 'Update Available',
      message:
        'A new version of the application has been deployed. ' +
        'Please reload now to get the latest features and fixes.',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Reload Now',
          handler: () => {
            window.location.reload();
          }
        },
        {
          text: 'Later',
          role: 'cancel',
          handler: () => {
            this.alertOpen = false;
          }
        }
      ]
    });
    await alert.present();
  }

  ngOnDestroy(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
    }
  }
}
